import React, { useState, useEffect } from 'react';
import { ChevronRight, Home, RefreshCw, Download } from 'lucide-react';
import { apiService } from '../../services/api.service';
import { toast } from 'react-hot-toast';

type LevelType = 'DISTRICT' | 'MANDAL' | 'VILLAGE' | 'BENEFICIARY';

interface DrillLevel {
  type: LevelType;
  district?: string;
  mandal?: string;
  village?: string;
}

const CombinedSummary: React.FC = () => {
  const [currentLevel, setCurrentLevel] = useState<DrillLevel>({ type: 'DISTRICT' });
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<DrillLevel[]>([{ type: 'DISTRICT' }]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  // Stage-Flow bucket modal (clickable TOTAL -> beneficiary list)
  type BucketCtx = { open: boolean; bucket: string; label: string; district?: string; mandal?: string; village?: string; stage?: string };
  const [bucketModal, setBucketModal] = useState<BucketCtx>({ open: false, bucket: '', label: '' });
  const [bucketRows, setBucketRows] = useState<any[]>([]);
  const [bucketLoading, setBucketLoading] = useState(false);
  const [bucketPage, setBucketPage] = useState(1);
  const [bucketTotal, setBucketTotal] = useState(0);
  const [bucketTotalPages, setBucketTotalPages] = useState(1);
  useEffect(() => { fetchData(); }, [currentLevel, currentPage]);

  const fetchBucket = async (ctx: BucketCtx, page: number) => {
    setBucketLoading(true);
    try {
      const params: any = { page, limit: 50 };
      if (ctx.district) params.district = ctx.district;
      if (ctx.mandal) params.mandal = ctx.mandal;
      if (ctx.village) params.village = ctx.village;
      if (ctx.stage) params.stage = ctx.stage;
      const res = await apiService.getStageFlowBucket(ctx.bucket, params);
      setBucketRows(res.beneficiaries || []);
      setBucketTotal(res.pagination?.total || 0);
      setBucketTotalPages(res.pagination?.totalPages || 1);
    } catch { toast.error('Failed to load beneficiary list'); }
    finally { setBucketLoading(false); }
  };

  const openBucketModal = (ctx: Omit<BucketCtx, 'open'>) => {
    const full: BucketCtx = { ...ctx, open: true };
    setBucketModal(full);
    setBucketPage(1);
    setBucketRows([]);
    fetchBucket(full, 1);
  };

  const changeBucketPage = (p: number) => {
    if (p < 1 || p > bucketTotalPages) return;
    setBucketPage(p);
    fetchBucket(bucketModal, p);
  };

  // Geo scope for a drill-down table row at the current level (the row IS the deepest geo shown).
  const rowGeo = (item: any) => ({
    district: currentLevel.type === 'DISTRICT' ? item.district : currentLevel.district,
    mandal: currentLevel.type === 'MANDAL' ? item.mandal : currentLevel.mandal,
    village: currentLevel.type === 'VILLAGE' ? item.village : currentLevel.village,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      let response;
      switch (currentLevel.type) {
        case 'DISTRICT':
          response = await apiService.getCombinedDistrictSummary();
          setData(response.data || []);
          break;
        case 'MANDAL':
          response = await apiService.getCombinedMandalSummary(currentLevel.district!);
          setData(response.data || []);
          break;
        case 'VILLAGE':
          response = await apiService.getCombinedVillageSummary(currentLevel.district!, currentLevel.mandal!);
          setData(response.data || []);
          break;
        case 'BENEFICIARY':
          response = await apiService.getCombinedBeneficiaryDrillDown(
            currentLevel.district!, currentLevel.mandal!, currentLevel.village!,
            { page: currentPage, limit: 50 }
          );
          setData(response.beneficiaries || []);
          if (response.pagination) {
            setTotalPages(response.pagination.totalPages);
            setTotalRecords(response.pagination.total);
          }
          break;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDrillDown = (item: any) => {
    let newLevel: DrillLevel;
    if (currentLevel.type === 'DISTRICT') {
      newLevel = { type: 'MANDAL', district: item.district };
    } else if (currentLevel.type === 'MANDAL') {
      newLevel = { type: 'VILLAGE', district: currentLevel.district, mandal: item.mandal };
    } else if (currentLevel.type === 'VILLAGE') {
      newLevel = { type: 'BENEFICIARY', district: currentLevel.district, mandal: currentLevel.mandal, village: item.village };
    } else return;

    setCurrentLevel(newLevel);
    setBreadcrumbs([...breadcrumbs, newLevel]);
    setCurrentPage(1);
  };

  const handleBreadcrumbClick = (index: number) => {
    const nb = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(nb);
    setCurrentLevel(nb[nb.length - 1]);
    setCurrentPage(1);
  };

  const getBreadcrumbName = (c: DrillLevel): string => {
    if (c.type === 'DISTRICT') return 'All Districts';
    if (c.type === 'MANDAL') return c.district || '';
    if (c.type === 'VILLAGE') return c.mandal || '';
    return c.village || '';
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params: { district?: string; mandal?: string } = {};
      if (currentLevel.district) params.district = currentLevel.district;
      if (currentLevel.mandal) params.mandal = currentLevel.mandal;

      const blob = await apiService.exportCombinedSummary(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = currentLevel.type === 'DISTRICT' ? 'Combined_Summary_All_Districts.xlsx'
        : currentLevel.type === 'MANDAL' ? `Combined_Summary_Mandals_${currentLevel.district}.xlsx`
        : `Combined_Summary_Villages_${currentLevel.district}_${currentLevel.mandal}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const formatAmount = (amount: number) => {
    if (!amount || amount === 0) return '₹0';
    const crores = amount / 10000000;
    if (crores >= 1) return `₹${crores.toFixed(2)} Cr`;
    const lakhs = amount / 100000;
    return `₹${lakhs.toFixed(2)} L`;
  };

  const geoField = currentLevel.type === 'DISTRICT' ? 'district' : currentLevel.type === 'MANDAL' ? 'mandal' : 'village';
  const canDrill = currentLevel.type !== 'BENEFICIARY';
  const isAggregate = currentLevel.type !== 'BENEFICIARY';

  const totals = data.reduce((acc, item) => ({
    totalBeneficiaries: acc.totalBeneficiaries + (parseInt(item.totalBeneficiaries) || 0),
    pennyDropDone: acc.pennyDropDone + (parseInt(item.pennyDropDone) || 0),
    pennyDropFailed: acc.pennyDropFailed + (parseInt(item.pennyDropFailed) || 0),
    paidNoPennyDrop: acc.paidNoPennyDrop + (parseInt(item.paidNoPennyDrop) || 0),
    paidAmountRupees: acc.paidAmountRupees + (parseFloat(item.paidAmountRupees) || 0),
    stagePD: acc.stagePD + (parseInt(item.stagePD) || 0),
    stageBL: acc.stageBL + (parseInt(item.stageBL) || 0),
    stageRL: acc.stageRL + (parseInt(item.stageRL) || 0),
    stageRC: acc.stageRC + (parseInt(item.stageRC) || 0),
    notPaid: acc.notPaid + (parseInt(item.notPaid) || 0),
    mdApproved: acc.mdApproved + (parseInt(item.mdApproved) || 0),
    mdLatestBlPaid: acc.mdLatestBlPaid + (parseInt(item.mdLatestBlPaid) || 0),
    mdLatestBlAtBank: acc.mdLatestBlAtBank + (parseInt(item.mdLatestBlAtBank) || 0),
    mdLatestBlFailed: acc.mdLatestBlFailed + (parseInt(item.mdLatestBlFailed) || 0),
    mdLatestBlPending: acc.mdLatestBlPending + (parseInt(item.mdLatestBlPending) || 0),
    mdLatestRlPaid: acc.mdLatestRlPaid + (parseInt(item.mdLatestRlPaid) || 0),
    mdLatestRlAtBank: acc.mdLatestRlAtBank + (parseInt(item.mdLatestRlAtBank) || 0),
    mdLatestRlFailed: acc.mdLatestRlFailed + (parseInt(item.mdLatestRlFailed) || 0),
    mdLatestRlPending: acc.mdLatestRlPending + (parseInt(item.mdLatestRlPending) || 0),
    mdLatestRcPaid: acc.mdLatestRcPaid + (parseInt(item.mdLatestRcPaid) || 0),
    mdLatestRcAtBank: acc.mdLatestRcAtBank + (parseInt(item.mdLatestRcAtBank) || 0),
    mdLatestRcFailed: acc.mdLatestRcFailed + (parseInt(item.mdLatestRcFailed) || 0),
    mdLatestRcPending: acc.mdLatestRcPending + (parseInt(item.mdLatestRcPending) || 0),
    bensPaidAny: acc.bensPaidAny + (parseInt(item.bensPaidAny) || 0),
    bensAtBankAny: acc.bensAtBankAny + (parseInt(item.bensAtBankAny) || 0),
    bensFailedAny: acc.bensFailedAny + (parseInt(item.bensFailedAny) || 0),
    bensHeldAny: acc.bensHeldAny + (parseInt(item.bensHeldAny) || 0),
    bensTrulyPending: acc.bensTrulyPending + (parseInt(item.bensTrulyPending) || 0),
    bensAtBankBL: acc.bensAtBankBL + (parseInt(item.bensAtBankBL) || 0),
    bensAtBankRL: acc.bensAtBankRL + (parseInt(item.bensAtBankRL) || 0),
    bensAtBankRC: acc.bensAtBankRC + (parseInt(item.bensAtBankRC) || 0),
    bensFailedBL: acc.bensFailedBL + (parseInt(item.bensFailedBL) || 0),
    bensFailedRL: acc.bensFailedRL + (parseInt(item.bensFailedRL) || 0),
    bensFailedRC: acc.bensFailedRC + (parseInt(item.bensFailedRC) || 0),
    bensPendingBL: acc.bensPendingBL + (parseInt(item.bensPendingBL) || 0),
    bensPendingRL: acc.bensPendingRL + (parseInt(item.bensPendingRL) || 0),
    bensPendingRC: acc.bensPendingRC + (parseInt(item.bensPendingRC) || 0),
    bensInflightBL: acc.bensInflightBL + (parseInt(item.bensInflightBL) || 0),
    bensInflightRL: acc.bensInflightRL + (parseInt(item.bensInflightRL) || 0),
    bensInflightRC: acc.bensInflightRC + (parseInt(item.bensInflightRC) || 0),
    mdAtPD: acc.mdAtPD + (parseInt(item.mdAtPD) || 0),
    mdAtBL: acc.mdAtBL + (parseInt(item.mdAtBL) || 0),
    mdAtRL: acc.mdAtRL + (parseInt(item.mdAtRL) || 0),
    mdAtRC: acc.mdAtRC + (parseInt(item.mdAtRC) || 0),
    mdAtCompleted: acc.mdAtCompleted + (parseInt(item.mdAtCompleted) || 0),
    mdNotPaid: acc.mdNotPaid + (parseInt(item.mdNotPaid) || 0),
    mdNotAtBank: acc.mdNotAtBank + (parseInt(item.mdNotAtBank) || 0),
    mdAtBankNoSuccess: acc.mdAtBankNoSuccess + (parseInt(item.mdAtBankNoSuccess) || 0),
    bensAwaitingBankResponse: acc.bensAwaitingBankResponse + (parseInt(item.bensAwaitingBankResponse) || 0),
    bensAwaitingBL: acc.bensAwaitingBL + (parseInt(item.bensAwaitingBL) || 0),
    bensAwaitingRL: acc.bensAwaitingRL + (parseInt(item.bensAwaitingRL) || 0),
    bensAwaitingRC: acc.bensAwaitingRC + (parseInt(item.bensAwaitingRC) || 0),
    stageBlMdApproved: acc.stageBlMdApproved + (parseInt(item.stageBlMdApproved) || 0),
    stageBlPaid: acc.stageBlPaid + (parseInt(item.stageBlPaid) || 0),
    stageBlFailed: acc.stageBlFailed + (parseInt(item.stageBlFailed) || 0),
    stageRlMdApproved: acc.stageRlMdApproved + (parseInt(item.stageRlMdApproved) || 0),
    stageRlPaid: acc.stageRlPaid + (parseInt(item.stageRlPaid) || 0),
    stageRlFailed: acc.stageRlFailed + (parseInt(item.stageRlFailed) || 0),
    stageRcMdApproved: acc.stageRcMdApproved + (parseInt(item.stageRcMdApproved) || 0),
    stageRcPaid: acc.stageRcPaid + (parseInt(item.stageRcPaid) || 0),
    stageRcFailed: acc.stageRcFailed + (parseInt(item.stageRcFailed) || 0),
    bensPendingGm: acc.bensPendingGm + (parseInt(item.bensPendingGm) || 0),
    bensFailedSf: acc.bensFailedSf + (parseInt(item.bensFailedSf) || 0),
  }), { totalBeneficiaries: 0, pennyDropDone: 0, pennyDropFailed: 0, paidNoPennyDrop: 0, paidAmountRupees: 0, stagePD: 0, stageBL: 0, stageRL: 0, stageRC: 0, notPaid: 0, mdApproved: 0, mdLatestBlPaid: 0, mdLatestBlAtBank: 0, mdLatestBlFailed: 0, mdLatestBlPending: 0, mdLatestRlPaid: 0, mdLatestRlAtBank: 0, mdLatestRlFailed: 0, mdLatestRlPending: 0, mdLatestRcPaid: 0, mdLatestRcAtBank: 0, mdLatestRcFailed: 0, mdLatestRcPending: 0, mdAtPD: 0, mdAtBL: 0, mdAtRL: 0, mdAtRC: 0, mdAtCompleted: 0, mdNotPaid: 0, mdNotAtBank: 0, mdAtBankNoSuccess: 0, bensPaidAny: 0, bensAtBankAny: 0, bensFailedAny: 0, bensHeldAny: 0, bensTrulyPending: 0, bensAtBankBL: 0, bensAtBankRL: 0, bensAtBankRC: 0, bensFailedBL: 0, bensFailedRL: 0, bensFailedRC: 0, bensPendingBL: 0, bensPendingRL: 0, bensPendingRC: 0, bensInflightBL: 0, bensInflightRL: 0, bensInflightRC: 0, bensAwaitingBankResponse: 0, bensAwaitingBL: 0, bensAwaitingRL: 0, bensAwaitingRC: 0, stageBlMdApproved: 0, stageBlPaid: 0, stageBlFailed: 0, stageRlMdApproved: 0, stageRlPaid: 0, stageRlFailed: 0, stageRcMdApproved: 0, stageRcPaid: 0, stageRcFailed: 0, bensPendingGm: 0, bensFailedSf: 0 });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Combined Geographic Summary</h1>
            <p className="mt-1 text-sm text-gray-600">Penny Drop + Payment + Failure status at District → Mandal → Village</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleExport} disabled={exporting || loading || data.length === 0}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
              <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-bounce' : ''}`} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
            <button onClick={fetchData} disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
            </button>
          </div>
        </div>

        {/* Breadcrumbs */}
        <div className="flex items-center space-x-2 mb-6 text-sm">
          <Home className="w-4 h-4 cursor-pointer text-blue-600 hover:text-blue-800" onClick={() => handleBreadcrumbClick(0)} />
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className={`cursor-pointer transition-colors ${index === breadcrumbs.length - 1 ? 'text-gray-900 font-semibold' : 'text-blue-600 hover:text-blue-800'}`}
                onClick={() => handleBreadcrumbClick(index)}>{getBreadcrumbName(crumb)}</span>
            </React.Fragment>
          ))}
        </div>

        {!loading && isAggregate && data.length > 0 && (
          <div className="mb-6 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Beneficiaries</div>
                <div className="mt-1 text-2xl font-bold text-gray-900">{totals.totalBeneficiaries.toLocaleString('en-IN')}</div>
              </div>
              <div className="bg-white border border-blue-200 rounded-lg p-4 shadow-sm">
                <div className="text-xs font-medium text-blue-600 uppercase tracking-wider">Penny Drop Done</div>
                <div className="mt-1 text-2xl font-bold text-blue-700">{totals.pennyDropDone.toLocaleString('en-IN')}</div>
              </div>
              <div className="bg-white border border-red-200 rounded-lg p-4 shadow-sm">
                <div className="text-xs font-medium text-red-600 uppercase tracking-wider">Penny Drop Failed</div>
                <div className="mt-1 text-2xl font-bold text-red-700">{totals.pennyDropFailed.toLocaleString('en-IN')}</div>
                <div className="text-[10px] text-gray-500 mt-1">attempted, never '00'</div>
              </div>
              <div className="bg-white border border-green-200 rounded-lg p-4 shadow-sm">
                <div className="text-xs font-medium text-green-600 uppercase tracking-wider">Paid Amount</div>
                <div className="mt-1 text-2xl font-bold text-green-700">{formatAmount(totals.paidAmountRupees)}</div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
              <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Stage Flow — MD Approved → Sent to Bank → Bank Response</span>
                <span className="text-[10px] text-gray-500">Not normalised — a beneficiary counts at every stage they reach (BL + RL + RC ≠ unique total)</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-[10px] text-gray-500 uppercase tracking-wider border-b border-gray-200">
                      <th className="px-3 py-2 text-left">Stage</th>
                      <th className="px-3 py-2 text-right">MD Approved</th>
                      <th className="px-3 py-2 text-right" title="MD-approved but no REQ sent to the bank yet (no payment, not in flight) — pending the GM's risk review & REQ generation">Pending @ GM</th>
                      <th className="px-3 py-2 text-right text-amber-700" title="In a REQ file sent to the bank, no response (RES) file yet">Sent → Awaiting</th>
                      <th className="px-3 py-2 text-right text-green-700">Paid</th>
                      <th className="px-3 py-2 text-right text-rose-700">Failed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {([
                      { key: 'BL', approved: totals.stageBlMdApproved, paid: totals.stageBlPaid, failed: totals.stageBlFailed, awaiting: totals.bensAwaitingBL },
                      { key: 'RL', approved: totals.stageRlMdApproved, paid: totals.stageRlPaid, failed: totals.stageRlFailed, awaiting: totals.bensAwaitingRL },
                      { key: 'RC', approved: totals.stageRcMdApproved, paid: totals.stageRcPaid, failed: totals.stageRcFailed, awaiting: totals.bensAwaitingRC },
                    ] as const).map((r) => {
                      const notSent = r.approved - r.paid - r.failed - r.awaiting;
                      return (
                        <tr key={r.key}>
                          <td className="px-3 py-2 font-bold text-slate-700">{r.key}</td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-900">{r.approved.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2 text-right text-gray-500">{notSent.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2 text-right text-amber-700">{r.awaiting.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2 text-right font-semibold text-green-700">{r.paid.toLocaleString('en-IN')}</td>
                          <td className="px-3 py-2 text-right text-rose-700">{r.failed.toLocaleString('en-IN')}</td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold">
                      <td className="px-3 py-2 text-slate-800">TOTAL</td>
                      <td className="px-3 py-2 text-right text-gray-300" title="Not summed — a beneficiary is approved at multiple stages, so a total would double-count">—</td>
                      <td className="px-3 py-2 text-right text-gray-700"><button type="button" onClick={() => openBucketModal({ bucket: 'pending_gm', label: 'Pending @ GM', district: currentLevel.district, mandal: currentLevel.mandal, village: currentLevel.village })} className="underline decoration-dotted underline-offset-2 hover:text-blue-700 cursor-pointer" title="Click to list these beneficiaries">{totals.bensPendingGm.toLocaleString('en-IN')}</button></td>
                      <td className="px-3 py-2 text-right text-amber-800"><button type="button" onClick={() => openBucketModal({ bucket: 'awaiting', label: 'Awaiting Bank Response', district: currentLevel.district, mandal: currentLevel.mandal, village: currentLevel.village })} className="underline decoration-dotted underline-offset-2 hover:text-blue-700 cursor-pointer" title="Click to list these beneficiaries">{totals.bensAwaitingBankResponse.toLocaleString('en-IN')}</button></td>
                      <td className="px-3 py-2 text-right text-gray-300" title="Not summed — a beneficiary can be paid at multiple stages, so a total would double-count">—</td>
                      <td className="px-3 py-2 text-right text-rose-800"><button type="button" onClick={() => openBucketModal({ bucket: 'failed', label: 'Failed (bank rejected)', district: currentLevel.district, mandal: currentLevel.mandal, village: currentLevel.village })} className="underline decoration-dotted underline-offset-2 hover:text-blue-700 cursor-pointer" title="Click to list these beneficiaries">{totals.bensFailedSf.toLocaleString('en-IN')}</button></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-[10px] text-gray-500">
                Each row reconciles: Pending @ GM + Sent→Awaiting + Paid + Failed = MD Approved. "Sent → Awaiting" = in a REQ file at the bank, no RES yet. "Pending @ GM" = MD-approved but no REQ sent yet (no payment, not in flight) — awaiting the GM's risk review & REQ generation. Paid/Failed are cumulative for that stage. TOTAL shows distinct unique beneficiaries (a person counted once even if pending/failed at more than one stage), so it's smaller than the column sum. Click a TOTAL number to see the beneficiary list. MD Approved and Paid are blank — summing them across stages would double-count.
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" /></div>
        ) : !isAggregate ? (
          /* BENEFICIARY TABLE */
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Beneficiary ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Current Stage</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Penny Drop</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">BL</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">RL</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">RC</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((b: any, index: number) => {
                  const stageColors: Record<string, string> = {
                    'RC': 'bg-green-100 text-green-800',
                    'RL': 'bg-blue-100 text-blue-800',
                    'BL': 'bg-purple-100 text-purple-800',
                    'PD': 'bg-yellow-100 text-yellow-800',
                    'Not Paid': 'bg-red-100 text-red-800',
                  };
                  return (
                    <tr key={b.beneficiaryId || index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{b.beneficiaryId}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{b.applicantName}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${stageColors[b.currentStage] || 'bg-gray-100 text-gray-600'}`}>
                          {b.currentStage}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {b.pennyDropStatus === 'Done' ? <span className="text-green-600 font-semibold">Done</span>
                          : b.pennyDropStatus === 'Failed' ? <span className="text-red-600 font-semibold">Failed</span>
                          : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-4 py-3 text-center">{parseInt(b.blDone) ? <span className="text-green-600">&#10003;</span> : <span className="text-gray-300">-</span>}</td>
                      <td className="px-4 py-3 text-center">{parseInt(b.rlDone) ? <span className="text-green-600">&#10003;</span> : <span className="text-gray-300">-</span>}</td>
                      <td className="px-4 py-3 text-center">{parseInt(b.rcDone) ? <span className="text-green-600">&#10003;</span> : <span className="text-gray-300">-</span>}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-gray-800">{b.paidAmountFormatted || formatAmount(b.paidAmountRupees)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * 50 + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * 50, totalRecords)}</span> of{' '}
                  <span className="font-medium">{totalRecords}</span>
                </p>
                <nav className="inline-flex rounded-md shadow-sm -space-x-px">
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                    className="px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50">Previous</button>
                  <span className="px-4 py-2 border border-gray-300 bg-white text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                    className="px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50">Next</button>
                </nav>
              </div>
            )}
            {data.length === 0 && !loading && <div className="text-center py-12 text-gray-500">No beneficiaries found</div>}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th rowSpan={3} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-bottom">{currentLevel.type}</th>
                  <th rowSpan={3} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider align-bottom">Beneficiaries</th>
                  <th colSpan={2} className="px-4 py-3 text-center text-xs font-medium text-blue-600 uppercase tracking-wider border-l border-gray-200">Penny Drop</th>
                  <th colSpan={2} className="px-4 py-3 text-center text-xs font-medium text-indigo-700 uppercase tracking-wider border-l-2 border-indigo-300 bg-indigo-50">MD Approval</th>
                  <th colSpan={15} className="px-4 py-3 text-center text-xs font-medium text-slate-600 uppercase tracking-wider border-l-2 border-slate-300 bg-slate-50" title="Stage flow — per stage: Approved, Pending @ GM (no REQ sent yet), Awaiting (sent, no response), Paid, Failed. Reconciles: Pend@GM + Await + Paid + Failed = Appr. Not normalised: a beneficiary counts at every stage reached.">Stage Flow</th>
                  <th rowSpan={3} className="px-4 py-3 text-right text-xs font-medium text-green-600 uppercase tracking-wider border-l border-gray-200 align-bottom">Paid Amount</th>
                  {canDrill && <th rowSpan={3} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"></th>}
                </tr>
                <tr className="bg-gray-100">
                  <th rowSpan={2} className="px-4 py-1 text-right text-xs text-gray-500 border-l border-gray-200 align-bottom" title="Successful penny drop (responseCode = 00)">Paid</th>
                  <th rowSpan={2} className="px-4 py-1 text-right text-xs text-gray-500 align-bottom" title="Attempted penny drop, never got responseCode='00'">Failed</th>
                  <th rowSpan={2} className="px-3 py-1 text-right text-xs font-semibold text-indigo-700 border-l-2 border-indigo-300 bg-indigo-50 align-bottom" title="MD-approved (unique beneficiaries)">Total</th>
                  <th rowSpan={2} className="px-2 py-1 text-right text-xs text-green-700 bg-indigo-50 align-bottom" title="Got money at any stage">Paid</th>
                  <th colSpan={5} className="px-2 py-1 text-center text-xs font-semibold text-slate-700 border-l-2 border-slate-300 bg-slate-50">BL</th>
                  <th colSpan={5} className="px-2 py-1 text-center text-xs font-semibold text-slate-700 border-l border-slate-200 bg-slate-50">RL</th>
                  <th colSpan={5} className="px-2 py-1 text-center text-xs font-semibold text-slate-700 border-l border-slate-200 bg-slate-50">RC</th>
                </tr>
                <tr className="bg-gray-100">
                  {(['BL', 'RL', 'RC'] as const).map((st, i) => (
                    <React.Fragment key={st}>
                      <th className={`px-2 py-1 text-right text-[10px] text-gray-500 bg-slate-50 ${i === 0 ? 'border-l-2 border-slate-300' : 'border-l border-slate-200'}`} title={`MD-approved at ${st}`}>Appr</th>
                      <th className="px-2 py-1 text-right text-[10px] text-gray-600 bg-slate-50" title={`Pending @ GM — approved at ${st}, no REQ sent yet`}>Pend@GM</th>
                      <th className="px-2 py-1 text-right text-[10px] text-amber-600 bg-slate-50" title={`Sent to bank, awaiting response (${st})`}>Awaiting</th>
                      <th className="px-2 py-1 text-right text-[10px] text-green-700 bg-slate-50" title={`Paid at ${st}`}>Paid</th>
                      <th className="px-2 py-1 text-right text-[10px] text-rose-600 bg-slate-50" title={`Bank rejected (non-'00') at ${st}`}>Failed</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((item, index) => (
                  <tr key={`${item[geoField]}-${index}`} className={`hover:bg-gray-50 ${canDrill ? 'cursor-pointer' : ''} transition-colors`}
                    onClick={() => canDrill && handleDrillDown(item)}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{item[geoField]}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">{parseInt(item.totalBeneficiaries).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-600 font-semibold border-l border-gray-100">{parseInt(item.pennyDropDone).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm text-right text-red-500">{(parseInt(item.pennyDropFailed) || 0).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-3 text-sm text-right font-semibold text-indigo-700 border-l-2 border-indigo-200 bg-indigo-50/30">{parseInt(item.mdApproved || 0).toLocaleString('en-IN')}</td>
                    <td className="px-2 py-3 text-sm text-right text-green-700 bg-indigo-50/30">{parseInt(item.bensPaidAny || 0) > 0 ? parseInt(item.bensPaidAny).toLocaleString('en-IN') : <span className="text-gray-300">—</span>}</td>
                    {([
                      { stage: 'BL', appr: item.stageBlMdApproved, paid: item.stageBlPaid, failed: item.stageBlFailed, await: item.bensAwaitingBL },
                      { stage: 'RL', appr: item.stageRlMdApproved, paid: item.stageRlPaid, failed: item.stageRlFailed, await: item.bensAwaitingRL },
                      { stage: 'RC', appr: item.stageRcMdApproved, paid: item.stageRcPaid, failed: item.stageRcFailed, await: item.bensAwaitingRC },
                    ]).map((s, i) => {
                      const appr = parseInt(s.appr) || 0, paid = parseInt(s.paid) || 0, failed = parseInt(s.failed) || 0, awaiting = parseInt(s.await) || 0;
                      const pend = appr - paid - failed - awaiting;
                      const dash = <span className="text-gray-300">—</span>;
                      const geo = rowGeo(item);
                      const cellBtn = (val: number, bucket: string, label: string) => val > 0 ? (
                        <button type="button" className="underline decoration-dotted underline-offset-2 hover:text-blue-700 cursor-pointer"
                          onClick={(e) => { e.stopPropagation(); openBucketModal({ bucket, label: `${label} · ${item[geoField]} · ${s.stage}`, ...geo, stage: s.stage }); }}>
                          {val.toLocaleString('en-IN')}
                        </button>
                      ) : dash;
                      return (
                      <React.Fragment key={i}>
                        <td className={`px-2 py-3 text-xs text-right text-gray-700 bg-slate-50/40 ${i === 0 ? 'border-l-2 border-slate-200' : 'border-l border-slate-100'}`}>{appr > 0 ? appr.toLocaleString('en-IN') : dash}</td>
                        <td className="px-2 py-3 text-xs text-right text-gray-600 bg-slate-50/40">{cellBtn(pend, 'pending_gm', 'Pending @ GM')}</td>
                        <td className="px-2 py-3 text-xs text-right text-amber-600 bg-slate-50/40">{cellBtn(awaiting, 'awaiting', 'Awaiting Bank Response')}</td>
                        <td className="px-2 py-3 text-xs text-right font-semibold text-green-700 bg-slate-50/40">{paid > 0 ? paid.toLocaleString('en-IN') : dash}</td>
                        <td className="px-2 py-3 text-xs text-right text-rose-600 bg-slate-50/40">{cellBtn(failed, 'failed', 'Failed (bank rejected)')}</td>
                      </React.Fragment>
                      );
                    })}
                    <td className="px-4 py-3 text-sm text-right font-semibold text-green-700 border-l border-gray-100">{item.paidAmountFormatted || formatAmount(parseFloat(item.paidAmountRupees))}</td>
                    {canDrill && <td className="px-4 py-3 text-center"><ChevronRight className="w-4 h-4 text-blue-600 mx-auto" /></td>}
                  </tr>
                ))}

                {data.length > 0 && (
                  <tr className="bg-blue-50 font-bold border-t-2 border-blue-300">
                    <td className="px-4 py-3 text-sm text-gray-900">TOTAL</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">{totals.totalBeneficiaries.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm text-right text-blue-700 border-l border-gray-200">{totals.pennyDropDone.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-sm text-right text-red-600">{totals.pennyDropFailed.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-3 text-sm text-right font-bold text-indigo-800 border-l-2 border-indigo-300 bg-indigo-100">{totals.mdApproved.toLocaleString('en-IN')}</td>
                    <td className="px-2 py-3 text-sm text-right font-bold text-green-800 bg-indigo-100">{totals.bensPaidAny.toLocaleString('en-IN')}</td>
                    {([
                      { appr: totals.stageBlMdApproved, paid: totals.stageBlPaid, failed: totals.stageBlFailed, await: totals.bensAwaitingBL },
                      { appr: totals.stageRlMdApproved, paid: totals.stageRlPaid, failed: totals.stageRlFailed, await: totals.bensAwaitingRL },
                      { appr: totals.stageRcMdApproved, paid: totals.stageRcPaid, failed: totals.stageRcFailed, await: totals.bensAwaitingRC },
                    ]).map((s, i) => {
                      const pend = s.appr - s.paid - s.failed - s.await;
                      return (
                      <React.Fragment key={i}>
                        <td className={`px-2 py-3 text-xs text-right font-bold text-gray-800 bg-slate-100 ${i === 0 ? 'border-l-2 border-slate-300' : 'border-l border-slate-200'}`}>{s.appr.toLocaleString('en-IN')}</td>
                        <td className="px-2 py-3 text-xs text-right font-bold text-gray-700 bg-slate-100">{pend.toLocaleString('en-IN')}</td>
                        <td className="px-2 py-3 text-xs text-right font-bold text-amber-700 bg-slate-100">{s.await.toLocaleString('en-IN')}</td>
                        <td className="px-2 py-3 text-xs text-right font-bold text-green-800 bg-slate-100">{s.paid.toLocaleString('en-IN')}</td>
                        <td className="px-2 py-3 text-xs text-right font-bold text-rose-700 bg-slate-100">{s.failed.toLocaleString('en-IN')}</td>
                      </React.Fragment>
                      );
                    })}
                    <td className="px-4 py-3 text-sm text-right text-green-700 border-l border-gray-200">{formatAmount(totals.paidAmountRupees)}</td>
                    {canDrill && <td className="px-4 py-3 text-center">-</td>}
                  </tr>
                )}
              </tbody>
            </table>
            {data.length === 0 && !loading && <div className="text-center py-12 text-gray-500">No data available</div>}
          </div>
        )}

        {bucketModal.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setBucketModal({ open: false, bucket: '', label: '' })}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{bucketModal.label}</h3>
                  <p className="text-xs text-gray-500">
                    {bucketTotal.toLocaleString('en-IN')} beneficiaries
                    {bucketModal.village ? ` · ${bucketModal.village}` : bucketModal.mandal ? ` · ${bucketModal.mandal}` : bucketModal.district ? ` · ${bucketModal.district}` : ' · All districts'}
                    {bucketModal.stage ? ` · stage ${bucketModal.stage}` : ''}
                  </p>
                </div>
                <button onClick={() => setBucketModal({ open: false, bucket: '', label: '' })} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
              </div>
              <div className="overflow-auto flex-1">
                {bucketLoading ? (
                  <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>
                ) : bucketRows.length === 0 ? (
                  <div className="text-center py-16 text-gray-500">No beneficiaries</div>
                ) : (
                  <table className="min-w-full text-sm divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-[11px] text-gray-500 uppercase tracking-wider">
                        <th className="px-4 py-2 text-left">#</th>
                        <th className="px-4 py-2 text-left">Beneficiary ID</th>
                        <th className="px-4 py-2 text-left">Name</th>
                        <th className="px-4 py-2 text-left">District</th>
                        <th className="px-4 py-2 text-left">Mandal</th>
                        <th className="px-4 py-2 text-left">Village</th>
                        <th className="px-4 py-2 text-center">Stage</th>
                        {bucketModal.bucket === 'failed' && <th className="px-4 py-2 text-left">Failure Reason</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {bucketRows.map((b: any, i: number) => (
                        <tr key={`${b.beneficiaryId}-${i}`} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-400">{(bucketPage - 1) * 50 + i + 1}</td>
                          <td className="px-4 py-2 font-medium text-gray-900">{b.beneficiaryId}</td>
                          <td className="px-4 py-2 text-gray-700">{b.applicantName || '—'}</td>
                          <td className="px-4 py-2 text-gray-600">{b.district || '—'}</td>
                          <td className="px-4 py-2 text-gray-600">{b.mandal || '—'}</td>
                          <td className="px-4 py-2 text-gray-600">{b.village || '—'}</td>
                          <td className="px-4 py-2 text-center"><span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-700">{b.stage}</span></td>
                          {bucketModal.bucket === 'failed' && (
                            <td className="px-4 py-2 text-rose-700">
                              {b.reason ? <>{b.responseCode && <span className="font-mono text-[11px] text-gray-500 mr-1">[{b.responseCode}]</span>}{b.reason}</> : <span className="text-gray-300">—</span>}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 text-sm">
                <span className="text-gray-500">Page {bucketPage} of {bucketTotalPages}</span>
                <div className="flex gap-2">
                  <button disabled={bucketPage <= 1 || bucketLoading} onClick={() => changeBucketPage(bucketPage - 1)} className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">Prev</button>
                  <button disabled={bucketPage >= bucketTotalPages || bucketLoading} onClick={() => changeBucketPage(bucketPage + 1)} className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">Next</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CombinedSummary;
